import React, { createContext, useContext, useEffect, useMemo, useState } from "react";
import { useUserPreference } from "@/hooks/useUserPreference";

// Simple theme provider compatible with Vite (no next-themes).
// Supports an explicit "system" preference that follows the OS color scheme,
// while exposing the resolved (effective) theme for components that only
// care about light vs dark.

export type Theme = "light" | "dark" | "system";
export type ResolvedTheme = "light" | "dark";

export const THEME_PREF_KEY = "ui.theme";
export const THEME_LOCAL_STORAGE_KEY = "vite-ui-theme";

interface ThemeContextValue {
  /** Current preference, including "system". */
  theme: Theme;
  /** Resolved light/dark, after evaluating "system". */
  resolvedTheme: ResolvedTheme;
  setTheme: (theme: Theme) => void;
  /** True while the new theme is being persisted server-side. */
  isSaving: boolean;
}

const ThemeContext = createContext<ThemeContextValue | undefined>(undefined);

export function useTheme() {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error("useTheme must be used within ThemeProvider");
  return ctx;
}

interface ThemeProviderProps {
  children: React.ReactNode;
  attribute?: string; // kept for API compatibility
  defaultTheme?: Theme;
  enableSystem?: boolean; // kept for API compatibility — system is always supported
  disableTransitionOnChange?: boolean; // ignored
  storageKey?: string;
}

function isValidTheme(v: unknown): v is Theme {
  return v === "light" || v === "dark" || v === "system";
}

function getSystemTheme(): ResolvedTheme {
  if (typeof window === "undefined" || !window.matchMedia) return "light";
  return window.matchMedia("(prefers-color-scheme: dark)").matches
    ? "dark"
    : "light";
}

export function ThemeProvider({
  children,
  defaultTheme = "system",
  storageKey = THEME_LOCAL_STORAGE_KEY,
}: ThemeProviderProps) {
  const { value: theme, setValue: setTheme, isSaving } = useUserPreference<Theme>({
    key: THEME_PREF_KEY,
    defaultValue: defaultTheme,
    validate: isValidTheme,
    localStorageKey: storageKey,
    errorMessage: "Couldn't save your theme. Reverted to the previous value.",
  });

  // Track the OS preference so "system" reacts to changes live.
  const [systemTheme, setSystemTheme] = useState<ResolvedTheme>(() =>
    getSystemTheme()
  );

  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return;
    const mql = window.matchMedia("(prefers-color-scheme: dark)");
    const onChange = (e: MediaQueryListEvent) =>
      setSystemTheme(e.matches ? "dark" : "light");
    mql.addEventListener("change", onChange);
    return () => mql.removeEventListener("change", onChange);
  }, []);

  const resolvedTheme: ResolvedTheme =
    theme === "system" ? systemTheme : theme;

  useEffect(() => {
    if (typeof document === "undefined") return;
    const root = document.documentElement;
    if (resolvedTheme === "dark") root.classList.add("dark");
    else root.classList.remove("dark");
  }, [resolvedTheme]);

  const value = useMemo<ThemeContextValue>(
    () => ({ theme, resolvedTheme, setTheme, isSaving }),
    [theme, resolvedTheme, setTheme, isSaving]
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}
